import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { HeadToHeadQueryContext } from '../head-to-head-query-context';
import { AlertService } from '../../shared/alert/alert.service';
import { JhiEventManager } from '../../shared/core-utils';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';
import { GermplasmPairTraitModel } from '../model/germplasm-pair-trait.model';
import { ActivatedRoute, Router } from '@angular/router';
import { HeadToHeadQueryService } from '../head-to-head-query.service';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { finalize } from 'rxjs/internal/operators/finalize';
import { formatErrorList } from '../../shared/alert/format-error-list';
import { GermplasmPairTraitSearchRequestModel } from '../model/germplasm-pair-trait-search-request.model';
import { SORT_PREDICATE_NONE } from '../../germplasm-manager/germplasm-search-resolve-paging-params';

@Component({
    selector: 'jhi-select-traits',
    styleUrl: './select-traits-step.component.scss',
    templateUrl: './select-traits-step.component.html'
})
export class SelectTraitsStepComponent implements OnInit, OnDestroy {
    @Output() next = new EventEmitter<void>();
    @Output() back = new EventEmitter<void>();
    @Input() active = false;

    variableType = VARIABLE_TYPE_OPTION.ALL;
    VARIABLE_TYPE_OPTION = VARIABLE_TYPE_OPTION;

    itemsPerPage: number = 20;
    page: number;
    predicate: any;
    isAscending: boolean;
    isLoading: boolean;
    totalItems: number;
    previousPage: any;
    isSelectAll: boolean;
    traits: GermplasmPairTraitModel[] = [];
    selectedItems: Map<number, GermplasmPairTraitModel> = new Map<number, GermplasmPairTraitModel>();
    variableDirectionMap: Map<number, number> = new Map<number, number>();
    lastClickIndex: any;
    private subscriptionsManager: SubscriptionsManager = new SubscriptionsManager();

    constructor(private activatedRoute: ActivatedRoute,
                public queryContext: HeadToHeadQueryContext,
                public alertService: AlertService,
                private eventManager: JhiEventManager,
                private headToHeadQueryService: HeadToHeadQueryService,
                private router: Router) {
    }

    ngOnInit(): void {
        this.page = 1;
        this.previousPage = 1;
        this.totalItems = 0;
        this.isSelectAll = false;
        this.setDefaultSort();
        this.traits = [];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['active'] && this.active) {
            this.loadAll();
            this.page = 1;
            this.previousPage = 1;
        }
    }

    private setDefaultSort() {
        this.predicate = 'name';
        this.isAscending = true;
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    prevStep(): void {
        this.back.emit();
    }

    nextStep(): void {
        if (!this.disableNextStep()) {
            this.queryContext.traits = Array.from(this.selectedItems.values());

            this.next.emit();
        }
    }

    disableNextStep(): boolean {
        return !this.selectedItems || this.selectedItems.size === 0;
    }

    onSelectAll(isSelectAll) {
        this.isSelectAll = !isSelectAll;
        this.clearSelectedItems();
        if (this.isSelectAll) {
            this.isLoading = true;
            const request = new GermplasmPairTraitSearchRequestModel();
            request.standardEntryGIDs = [...this.queryContext.standardGIDs];
            request.testEntryGIDs = [...this.queryContext.testEntryGIDS];
            request.germplasmPairs = this.queryContext.germplasmPairs;
            if (this.variableType === VARIABLE_TYPE_OPTION.ALL) {
                request.experimentTypeIds = [1155, 1170];
            } else {
                request.experimentTypeIds = this.variableType === VARIABLE_TYPE_OPTION.TRAITS ? [1115] : [1170];
            }
            this.headToHeadQueryService.getGermplasmPairTraits(request,
                {
                    page: this.page - 1,
                    size: this.totalItems,
                    sort: this.getSort()
                }).pipe(finalize(() => {
                this.isLoading = false;
            })).subscribe(
                (res: HttpResponse<GermplasmPairTraitModel[]>) => {
                    res.body.forEach((trait) => {
                        this.selectedItems.set(trait.variableId, trait);
                    });
                }
            );
        }
    }

    onClearSort($event) {
        $event.preventDefault();
        this.clearSort();
        this.transition();
    }

    transition() {
        this.router.navigate(['./'], {
            queryParamsHandling: 'merge',
            queryParams: {
                page: this.page,
                size: this.itemsPerPage,
                sort: this.getSort()
            }, relativeTo: this.activatedRoute
        });
        this.loadAll();
    }

    onVariableTypeChange() {
        this.selectedItems.clear();
        this.variableDirectionMap.clear();
        this.loadAll();
    }

    loadAll() {
        this.isLoading = true;
        const request = new GermplasmPairTraitSearchRequestModel();
        request.standardEntryGIDs = [...this.queryContext.standardGIDs];
        request.testEntryGIDs = [...this.queryContext.testEntryGIDS];
        request.germplasmPairs = this.queryContext.germplasmPairs;
        if (this.variableType === VARIABLE_TYPE_OPTION.ALL) {
            request.experimentTypeIds = [1155, 1170];
        } else {
            request.experimentTypeIds = this.variableType === VARIABLE_TYPE_OPTION.TRAITS ? [1155] : [1170];
        }
        this.headToHeadQueryService.getGermplasmPairTraits(request,
            {
                page: this.page - 1,
                size: this.itemsPerPage,
                sort: this.getSort()
            }).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe(
            (res: HttpResponse<GermplasmPairTraitModel[]>) => this.onSuccess(res.body, res.headers)
        );
    }

    private onSuccess(data: GermplasmPairTraitModel[], headers) {
        this.totalItems = parseInt(headers.get('X-Total-Count'), 10);
        this.traits = data;
        this.traits.forEach((trait) => {
            if (this.variableDirectionMap.has(trait.variableId)) {
                trait.direction = this.variableDirectionMap.get(trait.variableId);
            } else {
                trait.direction = 1;
            }
        });
    }

    isPageSelected() {
        return this.size() && this.traits.every((traitModel: GermplasmPairTraitModel) => Boolean(this.selectedItems.get(traitModel.variableId)));
    }

    size() {
        return this.isSelectAll ? this.totalItems : this.selectedItems.size;
    }

    getSort() {
        return [this.predicate + ',' + (this.isAscending ? 'asc' : 'desc')];
    }

    private clearSort() {
        this.predicate = 'name';
        this.isAscending = true;
    }

    clearSelectedItems() {
        this.selectedItems.clear();
    }

    trackId(index: number, item: any) {
        return item.variableId;
    }

    selectDirection(traitModel: GermplasmPairTraitModel) {
        const trait = this.isSelected(traitModel);
        if (trait) {
            this.variableDirectionMap.set(trait.variableId, traitModel.direction);
        }
    }

    isSelected(traitModel: GermplasmPairTraitModel) {
        return this.selectedItems.get(traitModel.variableId);
    }

    toggleSelect($event, index, trait: GermplasmPairTraitModel, checkbox = false) {
        if (this.isSelectAll) {
            return;
        }
        if (!$event.ctrlKey && !checkbox) {
            this.clearSelectedItems();
        }
        let items;
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1,
                min = Math.min(this.lastClickIndex, index);
            items = this.traits.slice(min, max);
        } else {
            items = [trait];
            this.lastClickIndex = index;
        }
        const isClickedItemSelected = this.selectedItems.has(trait.variableId);
        for (const item of items) {
            if (isClickedItemSelected) {
                this.selectedItems.delete(item.variableId);
            } else {
                this.selectedItems.set(item.variableId, item);
            }
        }
    }

    onSelectPage() {
        if (this.isPageSelected()) {
            // remove all items
            this.traits.forEach((entry) => this.selectedItems.delete(entry.variableId));
        } else {
            // check remaining items
            this.traits.forEach((entry) => {
                this.selectedItems.set(entry.variableId, entry);
            });
        }
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.transition();
        }
    }

    resetTable() {
        this.page = 1;
        this.previousPage = 1;
        this.isSelectAll = false;
        this.loadAll();
        this.clearSelectedItems();
    }

}

enum VARIABLE_TYPE_OPTION {
    ALL = 'ALL',
    TRAITS = 'TRAITS',
    ANALYSIS = 'ANALYSIS'
}
